import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell
} from "recharts";

export default function DashboardView({
  totalRevenue,
  totalExpense,
  profit,
  cpa,
  totalSpend,
  activationRate,
  totalLeads,
  chartData,
  funnelData,
  sourceChartData,
  monthlyChartData,
  loadingAI,
  aiInsight,
  handleGenerateAI,
  roi,
  localInsight,
  uploadStatus,
  topCampaign,
  topAgentName,
  worstCpaCampaign,
  exportToCSV,
  exportToExcel
}) {
  const formatYAxis = (value) => {
    if (value === 0) return "0";
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value}`;
  };

  const formatTooltipCurrency = (value) => {
    return `₹${Number(value).toLocaleString("en-IN")}`;
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Upload Data Status Tracker */}
      <div className="flex flex-wrap items-center gap-3 bg-white shadow-sm p-4 rounded-xl text-sm font-medium border border-slate-200">
        <span className="text-slate-500 uppercase text-xs font-bold tracking-wider mr-2">Data Status:</span>
        <span className={`px-2.5 py-1 rounded-md border ${uploadStatus.agents ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>👥 Agents {uploadStatus.agents ? '✅' : '❌'}</span>
        <span className={`px-2.5 py-1 rounded-md border ${uploadStatus.campaigns ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>📢 Campaigns {uploadStatus.campaigns ? '✅' : '❌'}</span>
        <span className={`px-2.5 py-1 rounded-md border ${uploadStatus.leads ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>🎯 Leads {uploadStatus.leads ? '✅' : '❌'}</span>
        <span className={`px-2.5 py-1 rounded-md border ${uploadStatus.expenses ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>📉 Expenses {uploadStatus.expenses ? '✅' : '❌'}</span>
        <span className={`px-2.5 py-1 rounded-md border ${uploadStatus.revenue ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>💰 Revenue {uploadStatus.revenue ? '✅' : '❌'}</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-xs uppercase font-bold tracking-wider mb-1">Cost Per Acquisition</h3>
          <p className="text-3xl font-bold text-slate-900">₹{Number(cpa).toFixed(0)}</p>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-xs uppercase font-bold tracking-wider mb-1">Total Ad Spend</h3>
          <p className="text-3xl font-bold text-slate-900">₹{totalSpend}</p>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-xs uppercase font-bold tracking-wider mb-1">Activation Rate</h3>
          <p className="text-3xl font-bold text-slate-900">{activationRate}%</p>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-slate-500 text-xs uppercase font-bold tracking-wider mb-1">Total Leads</h3>
          <p className="text-3xl font-bold text-slate-900">{totalLeads}</p>
        </div>
        <div className={`p-5 rounded-xl shadow-sm border ${profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex justify-between items-start mb-1">
            <h3 className={`text-xs uppercase font-bold tracking-wider ${profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Profit</h3>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${roi > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
              ROI: {roi !== null ? `${roi}% ${roi > 0 ? '↑' : ''}` : <span className="opacity-80">No Spend Data</span>}
            </span>
          </div>
          <p className={`text-3xl font-bold ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>₹{profit}</p>
        </div>
      </div>

      {/* Cross Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100 shadow-sm">
          <h3 className="text-blue-800 text-xs uppercase font-bold tracking-wider mb-1">Top Campaign</h3>
          <p className="text-2xl font-bold text-blue-900">{topCampaign}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-100 shadow-sm">
          <h3 className="text-emerald-800 text-xs uppercase font-bold tracking-wider mb-1">Top Agent</h3>
          <p className="text-2xl font-bold text-emerald-900">{topAgentName}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 rounded-xl border border-red-100 shadow-sm">
          <h3 className="text-red-800 text-xs uppercase font-bold tracking-wider mb-1">Highest CPA Campaign</h3>
          <p className="text-2xl font-bold text-red-900">{worstCpaCampaign}</p>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-slate-900 text-white p-8 rounded-xl shadow-lg border border-slate-800">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            ✨ AI Executive Summary
          </h2>
          <button
            onClick={handleGenerateAI}
            disabled={loadingAI}
            className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm"
          >
            {loadingAI ? "Generating..." : "Generate Insights"}
          </button>
        </div>
        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 text-slate-200 leading-relaxed whitespace-pre-line min-h-[100px]">
          {loadingAI ? "Analyzing latest financial indicators..." : aiInsight || localInsight}
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-slate-800">Conversion Funnel</h2>
            <div className="flex gap-2">
              <button onClick={() => exportToCSV(funnelData, "conversion_funnel")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">CSV</button>
              <button onClick={() => exportToExcel(funnelData, "conversion_funnel")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">Excel</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart layout="vertical" data={funnelData} margin={{ top: 10, right: 40, left: 40, bottom: 10 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" stroke="#475569" tick={{ fill: "#475569", fontSize: 12 }} width={140} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} contentStyle={{ borderRadius: '8px', color: '#0f172a' }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {funnelData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={["#0f172a", "#1e293b", "#334155", "#475569"][index]} />
                ))}
                <LabelList dataKey="value" position="right" fill="#0f172a" fontSize={12} fontWeight="bold" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-slate-800">Campaign Spend vs Revenue</h2>
            <div className="flex gap-2">
              <button onClick={() => exportToCSV(sourceChartData, "campaign_spend_vs_revenue")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">CSV</button>
              <button onClick={() => exportToExcel(sourceChartData, "campaign_spend_vs_revenue")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">Excel</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={sourceChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="source" stroke="#475569" tick={{ fill: "#475569", fontSize: 12 }} />
              <YAxis stroke="#475569" tick={{ fill: "#475569", fontSize: 12 }} tickFormatter={formatYAxis} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} contentStyle={{ borderRadius: '8px', color: '#0f172a' }} formatter={formatTooltipCurrency} />
              <Legend wrapperStyle={{ paddingTop: "20px", fontSize: '12px' }} />
              <Bar dataKey="spend" fill="#94a3b8" radius={[4, 4, 0, 0]} name="Ad Spend (₹)" />
              <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} name="Revenue (₹)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-slate-800">Monthly Spend vs Revenue</h2>
            <div className="flex gap-2">
              <button onClick={() => exportToCSV(monthlyChartData, "monthly_spend_vs_revenue")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">CSV</button>
              <button onClick={() => exportToExcel(monthlyChartData, "monthly_spend_vs_revenue")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">Excel</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthlyChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" stroke="#475569" tick={{ fill: "#475569", fontSize: 12 }} />
              <YAxis stroke="#475569" tick={{ fill: "#475569", fontSize: 12 }} tickFormatter={formatYAxis} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} contentStyle={{ borderRadius: '8px', color: '#0f172a' }} formatter={formatTooltipCurrency} />
              <Legend wrapperStyle={{ paddingTop: "20px", fontSize: '12px' }} />
              <Bar dataKey="spend" fill="#94a3b8" radius={[4, 4, 0, 0]} name="Spend (₹)" />
              <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ fill: "#10b981", r: 4 }} name="Revenue (₹)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-slate-800">Actual Expense Breakdown</h2>
            <div className="flex gap-2">
              <button onClick={() => exportToCSV(chartData, "expense_breakdown")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">CSV</button>
              <button onClick={() => exportToExcel(chartData, "expense_breakdown")} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded transition-colors">Excel</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" stroke="#475569" tick={{ fill: "#475569", fontSize: 12 }} />
              <YAxis stroke="#475569" tick={{ fill: "#475569", fontSize: 12 }} tickFormatter={formatYAxis} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} contentStyle={{ borderRadius: '8px', color: '#0f172a' }} formatter={formatTooltipCurrency} />
              <Bar dataKey="amount" fill="#2563eb" radius={[4, 4, 0, 0]} name="Amount (₹)">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={["#0f172a", "#1e293b", "#334155", "#475569"][index % 4]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Upgrade Hint */}
      <div className="bg-slate-900 text-white p-8 rounded-xl shadow-lg border border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">🚀 Unlock Advanced Analytics</h3>
          <ul className="text-slate-400 space-y-1 font-medium text-sm ml-2">
            <li>• Agent rankings & detailed leaderboards</li>
            <li>• Real-time automated performance alerts</li>
            <li>• Detailed multi-touch ROI insights</li>
          </ul>
        </div>
        <button className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-3.5 rounded-lg font-bold transition-colors whitespace-nowrap shadow-sm">
          Upgrade to Pro
        </button>
      </div>
    </div>
  );
}